import { useMemo } from "react"
import { get, map, filter, sortBy } from "lodash"
import useResource, { LegacyQueryDefinition, QueryDefinition } from "hooks/resources"
import { createSelector } from "reselect"
import { ReduxState } from "model/state"
import { IsoDate } from "helpers/types/dates"
import { OK, ResourceResult } from "helpers/data-statuses"

const EMPTY_ARRAY: Model[] = []

export const MODELS_QUERY: QueryDefinition = {
  resource: "models",
  url: () => ({
    id: "models",
    query: "{ models { id, name } }",
  }),
}

export interface Model {
  id: string
  name: string
}

type MissingDataFiles = "predictedBeta" | "historicalBeta" | "securityLiquidity" | "securityMarketcap" | "composite"

export interface ModelFactor {
  id: string
  label: string
  category: string
  alias: string | null
}

export interface ModelCategory {
  id: string
  label: string
  singleFactor: boolean
  color: string
  background: string
}

export interface LinkedFactor {
  id: string
  factors: string[]
}

export interface ModelMetadata {
  id: string
  name: string
  supportedCurrencies: string[]
  missingDataFiles: MissingDataFiles[]
  defaultBenchmark: string
  topFactors: string[]
  linkedFactors: LinkedFactor[] | null
  categories: ModelCategory[]
  factors: ModelFactor[]
  availability: {
    currentDate: IsoDate
    securitiesStartDate: IsoDate
    unmodeledDates: IsoDate[]
  }
}

interface StructuredModelCategory {
  id: string
  label: string
  options: ModelFactor[]
}

export const METADATA_GRAPHQL_QUERY = `query ($modelId: String!) {
  model (id: $modelId) {
    id
    name
    supportedCurrencies
    missingDataFiles
    defaultBenchmark
    topFactors
    linkedFactors {
      id
      factors
    }
    categories {
      id
      label: name
      singleFactor
      color
      background: backgroundColor
    }
    factors {
      id
      label: name,
      category
      alias
    }
    availability {
      currentDate
      securitiesStartDate
      unmodeledDates
    }
  }
}`

export const MODEL_METADATA_QUERY: LegacyQueryDefinition<{ modelId: string }> = {
  resource: "modelMetadata",
  url: ({ modelId }) => {
    if (!modelId) {
      return null
    }
    return {
      id: `modelMetadata-${modelId}`,
      query: METADATA_GRAPHQL_QUERY,
      variables: { modelId },
    }
  },
}

export function useModels(): ResourceResult<Model[]> {
  return useResource<Model[]>(MODELS_QUERY, "models")
}

export function getModels(state: ReduxState): Model[] {
  return get(state, "global.data.models.data.models", EMPTY_ARRAY)
}

export const getSortedModels = createSelector(
  [getModels],
  (models) => sortBy(models, (model) => model.name),
)

// There can only be one model selected at a time so there is no need to have the modelId as part of the
// redux state path (resource)
export function useModelMetadata(modelId: string | null): ResourceResult<ModelMetadata> {
  return useResource<ModelMetadata>({
    resource: "modelMetadata",
    url: () => {
      if (!modelId) {
        return null
      }
      return {
        id: `modelMetadata-${modelId}`,
        query: METADATA_GRAPHQL_QUERY,
        variables: { modelId },
      }
    },
  }, "model")
}

export function useModelMetrics(modelId: string): ResourceResult<StructuredModelCategory[]> {
  const [modelMetadata, status] = useModelMetadata(modelId)

  return useMemo((): ResourceResult<StructuredModelCategory[]> => {
    if (status !== OK || !modelMetadata) {
      return [null, status]
    }
    const modelMetrics = map(modelMetadata.categories, ({ id: categoryId, label: categoryLabel }): StructuredModelCategory => ({
      id: categoryId,
      label: categoryLabel,
      options: filter(modelMetadata?.factors, ({ category: factorCategoryId }) => factorCategoryId === categoryId),
    }))
    return [modelMetrics, status]
  }, [modelMetadata, status])
}

export function useSortedModels(sortFn = (a: Model, b: Model) => a.name.localeCompare(b.name)): ResourceResult<Model[]> {
  const [models, modelStatus] = useModels()

  return useMemo(() => {
    if (modelStatus !== OK || !models) {
      return [null, modelStatus]
    }
    return [models.toSorted(sortFn), modelStatus]
  }, [models, modelStatus, sortFn])
}
